import WebsiteBehaviors from "../website-behaviors/index.js";
import LanguageBehavior from "../language-behavior.js";
import Selection from "../selection.js";
import {injectStyles, removeElement, triggerEvent} from "../utils/document.js";
import {waitUntil} from "../utils/async.js";
import DOMPurify from "dompurify";

class Westlaw extends WebsiteBehaviors {
	identifier() {
		return "Westlaw";
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'location' implicitly has an 'any' type.
	static test(location) {
		return (location.host === "next.westlaw.com" || location.host.endsWith(".next.westlaw.com"));
	}

	styles() {
		// language=CSS
		return `
			#co_selectedTextMenu:not(.pn-copying) {
				display: none;
			}
			#co_highlightMenu {
				display: none !important;
			}
			.co_hl {
				background-color: white !important;
				cursor: text !important;
			}
			#co_inlineNotes {
				display: none !important;
			}
			#co_documentNotes {
				display: none !important;
			}
			#co_selectedTextMenuListItem_AddToDock {
				display: none !important;
			}
			#co_selectedTextMenuListItem_AddNote {
				display: none !important;
			}
			#co_selectedTextMenuListItem_Highlight {
				display: none !important;
			}
		`;
	}

	// @ts-expect-error TS(7006) FIXME: Parameter 'event' implicitly has an 'any' type.
	async mouseUp(event, bypassHighlightRules, isSelectionEqual, isPageCoordEqual, page, processOrgProxy) {
		const lang = document.documentElement.lang;
		const languageRules = LanguageBehavior.getLanguageRules(lang);

		// Apply adjustments and validate the selection prior to continuing
		const validSelection = Selection.ProcessAndValidateSelection(languageRules, bypassHighlightRules);

		// Did not click on Westlaw menu citation options dropdown, close Westlaw menu
		// (either the down arrow to open the menu, or a menu option)
		const westlawMenu = event.target;
		if (!westlawMenu.closest("#co_copyWithRefMenuAnchor")
			&& !westlawMenu.closest("#co_copyWithRefMenuContainer")) {
			const tm = document.getElementById("co_selectedTextMenu");
			if (tm)
				tm.classList.remove("pn-copying");
		}

		// Display menu after a text selection has been made.
		return validSelection && !(isSelectionEqual && isPageCoordEqual);
	}

	getCopyButtonLabel() {
		return "Copy With Citation";
	}

	// @ts-expect-error TS(2416) FIXME: Property 'copySelectionToClipboard' in type 'Westl... Remove this comment to see the full error message
	async copySelectionToClipboard(selectedText, document, loadCitation) {
		// The rest will be handled in the main world. See initMainWorld()
		document.dispatchEvent(new CustomEvent("pn-copy-westlaw-selection"));

		// We're only showing Westlaw's copy popup, so we need to stop the "copied to clipboard" message from showing.
		return false;
	}

	isCitationStructured() {
		return false;
	}

	initMainWorld() {
		/**
		 * Custom event to get the citation
		 */
		document.addEventListener("pn-get-westlaw-citation", async () => {
			// Add some CSS to hide the "text is copied" alert
			// language=CSS
			const styles = `
				#coid_website_messagePlaceholder {
					display: none !important;
				}
			`;
			const stylesRef = injectStyles(document, styles);

			// eslint-disable-next-line @typescript-eslint/unbound-method
			const orig = navigator.clipboard.write;
			navigator.clipboard.write = async (data) => {
				// Get citation HTML string
				const blob = await data[0].getType("text/html");
				const text = await blob.text();
				const ce = new CustomEvent("pn-westlaw-citation", {detail: text});
				document.dispatchEvent(ce);
				// Restore original function
				navigator.clipboard.write = orig;
			};

			// Simulate a "click" on the "Copy with Reference" button on Westlaw site
			triggerEvent(document, "#co_selectedTextMenuListItem_CopyWithRef button", "click");

			// Wait for Westlaw's success "text is copied" alert to appear
			await waitUntil(() => document.getElementById("coid_website_messagePlaceholder")?.style.display === "block");

			// Westlaw seems to hide their "text is copied" alert with a timeout, so
			// don't wait for this to happen - clean it up separately
			setTimeout(async () => {
				await waitUntil(() => document.getElementById("coid_website_messagePlaceholder")?.style.display === "none");
				removeElement(document, stylesRef);
			}, 100);
		});

		/**
		 * Custom event to let users use Westlaw's "Copy with citation" button
		 */
		document.addEventListener("pn-copy-westlaw-selection", () => {
			const pnPopup = document.querySelector(".js-pn-project-topic-menu")?.shadowRoot?.getElementById("project-topic-menu");

			if (!pnPopup)
				return;

			const left = parseInt(pnPopup.style.left, 10);
			const top = parseInt(pnPopup.style.top, 10);

			triggerEvent(document, document.body, "mouseup");

			// Make the Westlaw menu show up now
			setTimeout(() => {
				// `co_selectedTextMenu` has position:absolute, but that's relative to `co_pageContainer`, which has
				// position:relative, so we need to adjust the "top" value accordingly.
				const offsetTop = document.getElementById("co_pageContainer")?.offsetTop;
				const westlawPopup = document.getElementById("co_selectedTextMenu");

				if (!westlawPopup || !offsetTop)
					return;

				westlawPopup.style.left = left + "px";
				westlawPopup.style.top = (top - offsetTop) + "px";
				westlawPopup.style.visibility = "visible";
				westlawPopup.classList.add("pn-copying");
			}, 300);
		});
	}

	async generateFreeformCitation(preferredStyle: string): Promise<{style?: string; citation: string}> {
		// 2023-04-21 Jinghan:
		// We need to use the citation generated by the "Copy with Reference"
		// button in Westlaw's highlight menu, because it will include pincites.
		// The "Copy citation" button on the page's toolbar won't have pincites
		// in it, since it's for the entire document, so we shouldn't use that.
		//
		// As of today, Westlaw's Copy with Reference button calls their API,
		// and places the result on the user's clipboard using
		// navigator.clipboard.write. We can't read from the clipboard without
		// requesting permissions (which may get denied), so we temporarily
		// replace navigator.clipboard.write with our own function.
		//
		// Our own function gets what Westlaw is writing to the clipboard,
		// extracts the HTML string version, dispatches a custom event, does NOT
		// execute the original clipboard write, and restores
		// navigator.clipboard.write.
		//
		// We listen for our custom event, and upon receiving the HTML string,
		// clean it up and use it as the citationHtml.
		let citationHtml = null;

		// Ask for code in the "main world" to get the citation for us
		document.dispatchEvent(new CustomEvent("pn-get-westlaw-citation"));

		let isCitationFinished = false;
		// Listen for our custom event which contains the citation
		document.addEventListener("pn-westlaw-citation", (event: CustomEvent) => {
			const t: HTMLTemplateElement = document.createElement("template");
			t.innerHTML = DOMPurify.sanitize(event.detail);

			// If "copyWithRefReference" is not there, let's fail (it's better than potentially showing the highlighted text or random stuff).
			const cwrr = t.content.querySelector(".copyWithRefReference");
			if (cwrr) {
				cwrr.querySelectorAll("[style]")
					.forEach(e => e.removeAttribute("style"));

				citationHtml = "<p>" + cwrr.innerHTML + "</p>";
			}

			// Notify that the citation is ready to be saved
			isCitationFinished = true;
		});

		// Wait for citation clean up to finish
		await waitUntil(() => isCitationFinished === true);

		// Get name of citation format, to be displayed in citation panel
		let style = null;
		const styleElement = document.getElementById("co_copyWithRefPref");
		if (styleElement) {
			style = styleElement.textContent?.trim() ?? "";

			// Get rid of parentheses (e.g. convert "(ALWD)" to "ALWD")
			const matches = /^\((.*)\)$/.exec(style);
			if (matches)
				style = matches[1].trim();

			if (style.toLowerCase() !== "westlaw")
				style = "Westlaw - " + style;
		}

		if (!citationHtml)
			throw new Error("PN: Could not get citation data");

		return style ? {citation: citationHtml, style} : {citation: citationHtml};
	}
}

export default Westlaw;
